/*============================================================================
  WSS — Auto-suggest search (v2)
  ----------------------------------------------------------------------------
  Attaches a predictive dropdown to every /search form (header, in-page
  search-bar, mobile drawer).

  Two-slot panel:
    - completions slot (top) : query-term completions from /search/suggest.json
    - results slot (below)   : products (with SKUs) + collections from the
                               server-rendered `search.suggest` template, which
                               honours the same wholesale / hidden-product
                               filtering as the results page (no prices shown).

  v2 features:
    - loading skeleton, client-side cache, matched-term highlight
    - zero-state on focus: recent (localStorage) + popular (theme setting)
    - WAI-ARIA combobox (aria-expanded / activedescendant / listbox+option)
    - de-noised analytics: search + view_item_list fire once per SETTLED term

  dataLayer (conventions from snippets/analytics-datalayer.liquid):
    - search           (flat, search_type:'predictive')     — on settle
    - view_item_list   (nested ecommerce, clear-then-push)   — on settle
    - select_item      (product click)                       — nested ecommerce
    - select_suggestion(collection / query / view_all click) — flat, ties back
  Prices are intentionally omitted from every item.
==============================================================================*/
(function ($) {
  'use strict';
  if (typeof $ === 'undefined') return;

  var CFG = window.WSS_SEARCH_SUGGEST || {};
  var FILTER = CFG.filter || '';
  var WRAP = CFG.wrapValue === true;
  var MIN = CFG.minChars || 2;
  var DEBOUNCE = CFG.debounceMs || 250;
  var SETTLE = CFG.settleMs || 1200;
  var VIEW = CFG.view || 'suggest';
  var TYPES = CFG.types || 'product,collection';
  var QUERY_LIMIT = CFG.queryLimit || 5;
  var RECENT_KEY = CFG.recentKey || 'wss_recent_searches';
  var RECENT_LIMIT = CFG.recentLimit || 6;
  var POPULAR = String(CFG.popularSearchesRaw || '')
    .split(/\r?\n/)
    .map(function (s) { return s.trim(); })
    .filter(Boolean)
    .slice(0, 8);
  var LIST_ID = 'search_suggestions';
  var LIST_NAME = 'Search Suggestions';

  // Session caches shared across all instances.
  var resultCache = {}; // buildQuery(term) -> html
  var queryCache = {};  // raw term         -> [{text, styled_text}]

  /* ---- helpers ---------------------------------------------------------- */

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function dlPush(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }
  // Clear the prior ecommerce object first (same pattern as pushEcommerce()).
  function dlEcommerce(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
  }

  // Rebuild the exact query the submit handler would (scripts-js.liquid).
  function buildQuery(term) {
    var q = WRAP ? ("'" + term + "'") : term;
    if (FILTER) q += ' ' + FILTER;
    return q;
  }

  function resultsUrl(term) {
    return '/search?q=' + encodeURIComponent(buildQuery(term)) + '&type=product';
  }

  function idx($a) { return parseInt($a.attr('data-index'), 10) || 0; }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function escapeRegExp(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

  /* ---- recents (localStorage) ------------------------------------------ */

  function getRecents() {
    try {
      var raw = window.localStorage.getItem(RECENT_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }
  function addRecent(term) {
    term = $.trim(term || '');
    if (!term) return;
    try {
      var arr = getRecents().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      arr.unshift(term);
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, RECENT_LIMIT)));
    } catch (e) {}
  }
  function clearRecents() {
    try { window.localStorage.removeItem(RECENT_KEY); } catch (e) {}
  }

  /* ---- skeleton markup -------------------------------------------------- */

  function skelRows(n, withThumb) {
    var s = '<div class="oak__search-suggest-skeleton" aria-hidden="true">';
    for (var i = 0; i < n; i++) {
      s += '<div class="oak__search-suggest-skel-row">';
      if (withThumb) s += '<div class="oak__search-suggest-skel-thumb"></div>';
      s += '<div class="oak__search-suggest-skel-lines">' +
             '<div class="oak__search-suggest-skel-line"></div>' +
             '<div class="oak__search-suggest-skel-line oak__search-suggest-skel-line--short"></div>' +
           '</div></div>';
    }
    return s + '</div>';
  }

  /* ---- instance --------------------------------------------------------- */

  var instanceCount = 0;

  function SearchSuggest(form) {
    var self = this;
    this.$form = $(form).addClass('oak__search-suggest-anchor');
    this.$input = this.$form.find('input[name="q"]').first();
    if (!this.$input.length) return;

    this.id = 'wss-suggest-' + (++instanceCount);
    this.$completions = $('<div class="oak__search-suggest-completions"></div>');
    this.$results = $('<div class="oak__search-suggest-results"></div>');
    this.$panel = $('<div class="oak__search-suggest" role="listbox"></div>')
      .attr('id', this.id + '-panel')
      .append(this.$completions)
      .append(this.$results);
    this.$live = $('<span class="oak__search-suggest-live" aria-live="polite"></span>');
    this.$form.append(this.$panel).append(this.$live);

    this.$input.attr({
      'role': 'combobox',
      'aria-autocomplete': 'list',
      'aria-expanded': 'false',
      'aria-haspopup': 'listbox',
      'aria-controls': this.id + '-panel',
      'autocomplete': 'off'
    });

    this.seq = 0;
    this.activeIndex = -1;
    this.lastTerm = '';
    this.lastTrackedTerm = '';
    this.settleTimer = null;
    this.xhrResults = null;
    this.xhrQueries = null;
    this.$options = $();

    this.$input.on('input', debounce(function () { self.onType(); }, DEBOUNCE));
    this.$input.on('keydown', function (e) { self.onKeydown(e); });
    this.$input.on('focus', function () { self.onFocus(); });
    this.$form.on('submit', function () { addRecent(self.currentTerm()); });

    $(document).on('mousedown.wssSuggest', function (e) {
      if (self.$form[0] && !self.$form[0].contains(e.target)) self.close();
    });

    this.$panel.on('click', '.oak__search-suggest-link, .oak__search-suggest-viewall', function (e) {
      if ($(this).attr('data-suggest-type') === 'viewall') e.preventDefault();
      self.handleSelection($(this), false);
    });
    this.$panel.on('click', '.oak__search-suggest-chip', function (e) {
      e.preventDefault();
      self.handleSelection($(this), false);
    });
    this.$panel.on('click', '.oak__search-suggest-clear', function (e) {
      e.preventDefault();
      clearRecents();
      self.showZeroState();
    });
  }

  SearchSuggest.prototype.currentTerm = function () { return $.trim(this.$input.val()); };

  SearchSuggest.prototype.panelTerm = function () {
    var t = this.$results.find('.oak__search-suggest-inner').attr('data-search-term');
    return t || this.lastTerm;
  };

  SearchSuggest.prototype.onFocus = function () {
    var term = this.currentTerm();
    if (term.length < MIN) this.showZeroState();
    else this.fetch(term);
  };

  SearchSuggest.prototype.onType = function () {
    var term = this.currentTerm();
    if (term.length < MIN) { this.showZeroState(); return; }
    if (term === this.lastTerm && this.$options.length) { this.open(); return; }
    this.lastTerm = term;
    this.fetch(term);
  };

  /* ---- zero-state ------------------------------------------------------- */

  SearchSuggest.prototype.showZeroState = function () {
    clearTimeout(this.settleTimer);
    this.lastTerm = '';
    this.$completions.empty();

    var html = '';
    var recents = getRecents();
    if (recents.length) {
      html += '<div class="oak__search-suggest-group">' +
        '<div class="oak__search-suggest-heading-row">' +
          '<p class="oak__search-suggest-heading">Recent</p>' +
          '<button type="button" class="oak__search-suggest-clear">Clear</button>' +
        '</div>' + this.chips(recents) + '</div>';
    }
    if (POPULAR.length) {
      html += '<div class="oak__search-suggest-group">' +
        '<p class="oak__search-suggest-heading">Popular</p>' + this.chips(POPULAR) + '</div>';
    }

    this.$results.html(html);
    this.rebuildOptions();
    if (html) this.open(); else this.close();
  };

  SearchSuggest.prototype.chips = function (terms) {
    var s = '<div class="oak__search-suggest-chips">';
    for (var i = 0; i < terms.length; i++) {
      var t = escapeHtml(terms[i]);
      s += '<button type="button" class="oak__search-suggest-chip" data-suggest-type="term" data-term="' + t + '">' + t + '</button>';
    }
    return s + '</div>';
  };

  /* ---- fetch / render --------------------------------------------------- */

  SearchSuggest.prototype.fetch = function (term) {
    var self = this;
    var seq = ++this.seq;
    clearTimeout(this.settleTimer);
    this.activeIndex = -1;

    var qKey = buildQuery(term);
    var haveResults = resultCache.hasOwnProperty(qKey);
    var haveQueries = queryCache.hasOwnProperty(term);

    this.renderSkeleton(!haveResults, !haveQueries);
    this.open();

    // Results slot
    if (haveResults) {
      this.fillResults(resultCache[qKey], term, seq);
    } else {
      if (this.xhrResults) this.xhrResults.abort();
      this.xhrResults = $.ajax({
        url: '/search', method: 'GET', dataType: 'html',
        data: { q: qKey, type: TYPES, view: VIEW }
      }).done(function (html) {
        if (seq !== self.seq) return;
        resultCache[qKey] = html;
        self.fillResults(html, term, seq);
      }).fail(function (jqxhr, status) {
        if (status === 'abort' || seq !== self.seq) return;
        self.fillResults('', term, seq);
      });
    }

    // Completions slot
    if (haveQueries) {
      this.fillQueries(queryCache[term], term, seq);
    } else {
      if (this.xhrQueries) this.xhrQueries.abort();
      this.xhrQueries = $.ajax({
        url: '/search/suggest.json', method: 'GET', dataType: 'json',
        data: { q: term, 'resources[type]': 'query', 'resources[limit]': QUERY_LIMIT }
      }).done(function (data) {
        if (seq !== self.seq) return;
        var qs = (((data || {}).resources || {}).results || {}).queries || [];
        queryCache[term] = qs;
        self.fillQueries(qs, term, seq);
      }).fail(function (jqxhr, status) {
        if (status === 'abort' || seq !== self.seq) return;
        self.fillQueries([], term, seq);
      });
    }
  };

  SearchSuggest.prototype.renderSkeleton = function (showResults, showQueries) {
    if (showQueries) this.$completions.html(skelRows(2, false));
    if (showResults) this.$results.html(skelRows(3, true));
  };

  SearchSuggest.prototype.fillResults = function (html, term, seq) {
    if (seq !== this.seq) return;
    this.$results.html(html || '');
    this.highlightTerm(this.$results, term);
    this.afterRender(term, seq);
  };

  SearchSuggest.prototype.fillQueries = function (queries, term, seq) {
    if (seq !== this.seq) return;
    if (!queries || !queries.length) {
      this.$completions.empty();
      this.afterRender(term, seq);
      return;
    }
    var lis = '';
    for (var i = 0; i < queries.length; i++) {
      var text = queries[i].text || '';
      var styled = queries[i].styled_text || escapeHtml(text);
      lis += '<li class="oak__search-suggest-item">' +
        '<a class="oak__search-suggest-link oak__search-suggest-link--query" href="' + resultsUrl(text) + '" ' +
           'data-suggest-type="query" data-item-name="' + escapeHtml(text) + '">' +
          '<span class="oak__search-suggest-thumb oak__search-suggest-thumb--query"><i class="fa-regular fa-magnifying-glass"></i></span>' +
          '<span class="oak__search-suggest-text"><span class="oak__search-suggest-title">' + styled + '</span></span>' +
        '</a></li>';
    }
    this.$completions.html(
      '<div class="oak__search-suggest-group">' +
        '<p class="oak__search-suggest-heading">Suggestions</p>' +
        '<ul class="oak__search-suggest-list">' + lis + '</ul></div>'
    );
    this.afterRender(term, seq);
  };

  SearchSuggest.prototype.afterRender = function (term, seq) {
    if (seq !== this.seq) return;
    this.rebuildOptions();

    // Nothing rendered at all (e.g. both requests failed) → close.
    if (!this.$results.children().length && !this.$completions.children().length) {
      this.close();
      return;
    }
    this.open();
    this.scheduleSettle();
  };

  SearchSuggest.prototype.rebuildOptions = function () {
    var self = this;
    this.$options = this.$panel.find('.oak__search-suggest-link, .oak__search-suggest-chip, .oak__search-suggest-viewall');
    this.$options.each(function (i) {
      this.id = self.id + '-opt-' + i;
      this.setAttribute('role', 'option');
      this.setAttribute('aria-selected', 'false');
    });
    this.activeIndex = -1;
    this.$input.removeAttr('aria-activedescendant');
  };

  /* ---- matched-term highlight ------------------------------------------ */

  SearchSuggest.prototype.highlightTerm = function ($container, term) {
    if (!term) return;
    var re = new RegExp('(' + escapeRegExp(escapeHtml(term)) + ')', 'ig');
    $container.find('.oak__search-suggest-link:not(.oak__search-suggest-link--query) .oak__search-suggest-title').each(function () {
      this.innerHTML = escapeHtml(this.textContent).replace(re, '<strong>$1</strong>');
    });
  };

  /* ---- selection -------------------------------------------------------- */

  SearchSuggest.prototype.handleSelection = function ($el, fromKeyboard) {
    var type = $el.attr('data-suggest-type');

    if (type === 'term') { // recent / popular chip
      this.applyTerm($el.attr('data-term'));
      return;
    }

    // Flush the impression + search event before the click event (CTR ordering).
    this.flushTracking();

    if (type === 'product') {
      dlEcommerce({
        event: 'select_item',
        ecommerce: {
          item_list_id: LIST_ID, item_list_name: LIST_NAME,
          items: [{ item_id: $el.attr('data-item-id'), item_name: $el.attr('data-item-name'), index: idx($el) }]
        }
      });
      addRecent(this.panelTerm());
      if (fromKeyboard) this.go($el.attr('href'));
    } else if (type === 'collection') {
      this.trackSuggestion('collection', this.panelTerm(), $el);
      addRecent(this.panelTerm());
      if (fromKeyboard) this.go($el.attr('href'));
    } else if (type === 'query') {
      var qterm = $el.attr('data-item-name');
      this.trackSuggestion('query', qterm);
      addRecent(qterm);
      if (fromKeyboard) this.go($el.attr('href'));
    } else if (type === 'viewall') {
      var vterm = this.currentTerm() || this.panelTerm();
      this.trackSuggestion('view_all', vterm);
      addRecent(vterm);
      this.close();
      this.go(resultsUrl(vterm));
    }
  };

  SearchSuggest.prototype.trackSuggestion = function (type, term, $el) {
    var payload = {
      event: 'select_suggestion',
      search_type: 'predictive',
      suggestion_type: type,
      item_list_id: LIST_ID,
      item_list_name: LIST_NAME,
      search_term: term
    };
    if ($el) {
      payload.item_id = $el.attr('data-item-id');
      payload.item_name = $el.attr('data-item-name');
      payload.index = idx($el);
    }
    dlPush(payload);
  };

  SearchSuggest.prototype.applyTerm = function (term) {
    if (!term) return;
    this.$input.val(term).focus();
    this.lastTerm = '';
    this.fetch(term);
  };

  SearchSuggest.prototype.go = function (href) { if (href) window.location.href = href; };

  /* ---- analytics settle ------------------------------------------------- */

  SearchSuggest.prototype.scheduleSettle = function () {
    var self = this;
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(function () { self.flushTracking(); }, SETTLE);
  };

  SearchSuggest.prototype.flushTracking = function () {
    var $inner = this.$results.find('.oak__search-suggest-inner');
    if (!$inner.length) return; // only skeleton / zero-state showing
    var term = $inner.attr('data-search-term') || this.lastTerm;
    if (!term || term === this.lastTrackedTerm) return;
    this.lastTrackedTerm = term;

    var count = parseInt($inner.attr('data-results-count'), 10) || 0;

    dlPush({ event: 'search', search_type: 'predictive', search_term: term, results_count: count });

    var items = [];
    this.$results.find('.oak__search-suggest-link[data-suggest-type="product"]').each(function () {
      var $a = $(this);
      items.push({ item_id: $a.attr('data-item-id'), item_name: $a.attr('data-item-name'), index: idx($a) });
    });
    if (items.length) {
      dlEcommerce({
        event: 'view_item_list',
        ecommerce: { item_list_id: LIST_ID, item_list_name: LIST_NAME, items: items }
      });
    }

    this.announce(count, term);
  };

  SearchSuggest.prototype.announce = function (count, term) {
    var msg = count > 0
      ? (count + ' result' + (count === 1 ? '' : 's') + ' for "' + term + '"')
      : ('No results for "' + term + '"');
    this.$live.text(msg);
  };

  /* ---- keyboard --------------------------------------------------------- */

  SearchSuggest.prototype.onKeydown = function (e) {
    if (!this.$panel.hasClass('oak__search-suggest--open') || !this.$options.length) return;

    if (e.keyCode === 40) {            // down
      e.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, this.$options.length - 1);
      this.highlightActive();
    } else if (e.keyCode === 38) {     // up
      e.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
      this.highlightActive();
    } else if (e.keyCode === 13) {     // enter
      if (this.activeIndex >= 0) {
        e.preventDefault();
        this.handleSelection(this.$options.eq(this.activeIndex), true);
      }
      // else: let the form's normal submit take over (full results page).
    } else if (e.keyCode === 27) {     // esc
      this.close();
    }
  };

  SearchSuggest.prototype.highlightActive = function () {
    this.$options.removeClass('oak__search-suggest-link--active oak__search-suggest-chip--active')
      .attr('aria-selected', 'false');
    if (this.activeIndex < 0) { this.$input.removeAttr('aria-activedescendant'); return; }
    var el = this.$options.eq(this.activeIndex);
    var isChip = el.hasClass('oak__search-suggest-chip');
    el.addClass(isChip ? 'oak__search-suggest-chip--active' : 'oak__search-suggest-link--active')
      .attr('aria-selected', 'true');
    this.$input.attr('aria-activedescendant', el.attr('id') || '');
    if (el[0] && el[0].scrollIntoView) el[0].scrollIntoView({ block: 'nearest' });
  };

  /* ---- open / close ----------------------------------------------------- */

  SearchSuggest.prototype.open = function () {
    if (this.$panel.children('.oak__search-suggest-completions, .oak__search-suggest-results')
      .children().length) {
      this.$panel.addClass('oak__search-suggest--open');
      this.$input.attr('aria-expanded', 'true');
    }
  };
  SearchSuggest.prototype.close = function () {
    this.$panel.removeClass('oak__search-suggest--open');
    this.$input.attr('aria-expanded', 'false').removeAttr('aria-activedescendant');
    this.activeIndex = -1;
  };

  /* ---- boot ------------------------------------------------------------- */

  $(function () {
    $('form[action="/search"]').each(function () {
      new SearchSuggest(this);
    });
  });
})(window.jQuery);
